import { smsSegments } from '@church/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApi } from '../api-context.tsx'
import { messagingKeys } from './keys.ts'

/**
 * The SMS inbox: threads newest first, one open at a time, and a reply box
 * that shows what the reply will cost. A thread whose number texted STOP is
 * read-only — the server refuses a reply, and the screen says why first.
 */
export function useSmsInbox() {
  const api = useApi()
  const queryClient = useQueryClient()
  const [openId, setOpenId] = useState<string | null>(null)
  const [reply, setReply] = useState('')

  const conversations = useQuery({
    queryKey: messagingKeys.conversations(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.sms.conversations.get()
      if (error) throw error
      return data
    },
    refetchInterval: 10_000,
  })
  const thread = useQuery({
    queryKey: messagingKeys.conversation(openId ?? ''),
    enabled: Boolean(openId),
    queryFn: async () => {
      const { data, error } = await api.api.v1.sms.conversations({ id: openId ?? '' }).get()
      if (error) throw error
      return data
    },
    refetchInterval: 10_000,
  })
  const send = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.sms
        .conversations({ id: openId ?? '' })
        .reply.post({ body: reply })
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      setReply('')
      queryClient.setQueryData(messagingKeys.conversation(data.id), data)
      void queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() })
    },
  })

  const open = (id: string) => {
    setOpenId(id)
    setReply('')
    // Opening marks it read on the server; mirror that in the list at once.
    void queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() })
  }

  return {
    conversations: conversations.data ?? [],
    isLoading: conversations.isPending,
    error: conversations.error ?? thread.error,
    openId,
    open,
    /** Back to the list (a phone shows one or the other). */
    close: () => setOpenId(null),
    thread: thread.data ?? null,
    reply,
    setReply,
    segments: reply ? smsSegments(reply) : null,
    canReply: Boolean(thread.data && !thread.data.optedOut && reply.trim()),
    send,
  }
}
